// Global User Interaction Logger
// Captures clicks/touches/keys/fullscreen/media events and logs them.
(function () {
  const state = {
    enabled: false,
    unsubscribers: [],
  };

  function summarizeTarget(t) {
    try {
      if (!t || !t.tagName) return 'n/a';
      const tag = t.tagName.toLowerCase();
      const id = t.id ? `#${t.id}` : '';
      const cls = t.classList && t.classList.length ? '.' + Array.from(t.classList).join('.') : '';
      return `${tag}${id}${cls}`;
    } catch (_) { return 'n/a'; }
  }

  function log(msg, extra) {
    try {
      const time = new Date().toISOString();
      const line = `[UI] ${time} ${msg}` + (extra ? ` ${extra}` : '');
      console.log(line);
      // Also buffer for export if overlay/console is blank
      if (Array.isArray(window.__USER_EVENT_BUFFER)) {
        window.__USER_EVENT_BUFFER.push(line);
        if (window.__USER_EVENT_BUFFER.length > 1000) window.__USER_EVENT_BUFFER.shift();
      }
    } catch (_) {}
  }

  function on(type, target, handler, options) {
    try {
      target.addEventListener(type, handler, options || true);
      state.unsubscribers.push(() => target.removeEventListener(type, handler, options || true));
    } catch (_) {}
  }

  function attachMediaEventLoggingTo(el) {
    try {
      if (!el) return;
      const tag = (el.tagName || '').toLowerCase();
      if (tag !== 'video' && tag !== 'audio') return;
      on('play', el, () => log(`media play`, `src=${safeSrc(el)} target=${summarizeTarget(el)}`));
      on('pause', el, () => log(`media pause`, `src=${safeSrc(el)} target=${summarizeTarget(el)}`));
      on('ended', el, () => log(`media ended`, `src=${safeSrc(el)} target=${summarizeTarget(el)}`));
      // iOS native fullscreen
      on('webkitbeginfullscreen', el, () => log(`webkitbeginfullscreen`, `src=${safeSrc(el)} target=${summarizeTarget(el)}`));
      on('webkitendfullscreen', el, () => log(`webkitendfullscreen`, `src=${safeSrc(el)} target=${summarizeTarget(el)}`));
    } catch (_) {}
  }

  function safeSrc(el) {
    try { return el.currentSrc || el.src || ''; } catch (_) { return ''; }
  }

  function enable() {
    if (state.enabled) return;
    state.enabled = true;
    if (!Array.isArray(window.__USER_EVENT_BUFFER)) window.__USER_EVENT_BUFFER = [];

    // Pointer/touch/mouse/keyboard
    on('click', document, (e) => log('click', `target=${summarizeTarget(e.target)}`));
    on('pointerdown', document, (e) => log('pointerdown', `target=${summarizeTarget(e.target)} x=${e.clientX} y=${e.clientY}`));
    on('pointerup', document, (e) => log('pointerup', `target=${summarizeTarget(e.target)} x=${e.clientX} y=${e.clientY}`));
    on('touchstart', document, (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      log('touchstart', `target=${summarizeTarget(e.target)} x=${t ? t.clientX : ''} y=${t ? t.clientY : ''}`);
    });
    on('touchend', document, (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      log('touchend', `target=${summarizeTarget(e.target)} x=${t ? t.clientX : ''} y=${t ? t.clientY : ''}`);
    });
    on('keydown', document, (e) => log('keydown', `key=${e.key} target=${summarizeTarget(e.target)}`));
    on('keyup', document, (e) => log('keyup', `key=${e.key} target=${summarizeTarget(e.target)}`));

    // Fullscreen and visibility
    on('fullscreenchange', document, () => {
      const fs = document.fullscreenElement;
      log('fullscreenchange', `active=${fs ? summarizeTarget(fs) : 'none'}`);
    });
    on('visibilitychange', document, () => log('visibilitychange', `hidden=${document.hidden ? 1 : 0}`));

    // Attach to existing media
    try { Array.from(document.querySelectorAll('video,audio')).forEach(attachMediaEventLoggingTo); } catch (_) {}

    // Observe future media additions
    try {
      const mo = new MutationObserver((list) => {
        try {
          list.forEach((m) => {
            if (m.type !== 'childList') return;
            m.addedNodes && m.addedNodes.forEach((n) => {
              if (!n || !n.querySelectorAll) return;
              if ((n.tagName || '').toLowerCase() === 'video' || (n.tagName || '').toLowerCase() === 'audio') {
                attachMediaEventLoggingTo(n);
              }
              Array.from(n.querySelectorAll('video,audio')).forEach(attachMediaEventLoggingTo);
            });
          });
        } catch (_) {}
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
      state.unsubscribers.push(() => { try { mo.disconnect(); } catch (_) {} });
    } catch (_) {}

    log('logger enabled');
  }

  function disable() {
    if (!state.enabled) return;
    state.enabled = false;
    // Remove all listeners/observers
    try { state.unsubscribers.forEach((fn) => fn && fn()); } catch (_) {}
    state.unsubscribers = [];
    log('logger disabled');
  }

  window.enableUserInteractionLogging = enable;
  window.disableUserInteractionLogging = disable;

  // Auto-enable to capture everything immediately for current debug session
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enable, { once: true });
  } else {
    enable();
  }
})();
